import { useCallback, useEffect, useState } from 'react';
import {
  borrarVentana, crearDinamica, crearVentanas, getAjustesDinamica, guardarAjustesDinamica,
  listDinamicasDeCampania, listVentanas,
  type AjustesDinamica, type DinamicaRow, type VentanaRow,
} from '../api/dinamicas';
import { listPdv } from '../api/pdv';
import type { PdvRow } from '../lib/db-types';

/** ISO 8601: 1 = lunes … 7 = domingo, la misma convención de `campaign_schedules`. */
const DIAS: { n: number; label: string }[] = [
  { n: 1, label: 'Lun' }, { n: 2, label: 'Mar' }, { n: 3, label: 'Mié' }, { n: 4, label: 'Jue' },
  { n: 5, label: 'Vie' }, { n: 6, label: 'Sáb' }, { n: 7, label: 'Dom' },
];

const dia = (n: number) => DIAS.find((d) => d.n === n)?.label ?? String(n);
const fecha = (f: string | null) => (f ? f.split('-').reverse().join('/') : null);

/** Cómo se lee una ventana en una línea, sin tener que interpretar nulos. */
function describir(v: VentanaRow): string {
  const donde = v.pdv ? v.pdv.name : 'Todas las tiendas';
  const desde = fecha(v.date_from);
  const hasta = fecha(v.date_to);
  const cuando =
    desde && hasta ? `${desde} a ${hasta}`
      : desde ? `desde ${desde}`
        : hasta ? `hasta ${hasta}`
          : 'sin límite de fechas';
  const dias = v.weekdays && v.weekdays.length > 0
    ? v.weekdays.map(dia).join(', ')
    : 'todos los días';
  return `${donde} · ${cuando} · ${dias}`;
}

/**
 * Dónde y cuándo corre cada dinámica de la campaña.
 *
 * Antes esto solo se podía tocar por SQL, así que decidir en qué tiendas va una promoción
 * dependía de quien tuviera acceso a la base. Las dinámicas cambian de tienda y de fecha entre
 * campañas —y a veces dentro de la misma—, así que tiene que poder configurarlo el jefe de
 * operaciones.
 *
 * La consecuencia de esta pantalla se ve en el celular: una dinámica **solo aparece** en las
 * tiendas y los días que aquí se marquen. Por eso el aviso de "sin vigencias vale en todas
 * partes" está a la vista y no en una ayuda: es la diferencia entre una promoción acotada y una
 * que se ofrece donde no existe.
 */
export default function DinamicasCampania({ campaignId }: { campaignId: string }) {
  const [dinamicas, setDinamicas] = useState<DinamicaRow[]>([]);
  const [ventanas, setVentanas] = useState<VentanaRow[]>([]);
  const [pdvs, setPdvs] = useState<PdvRow[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // Formulario de alta
  const [abierta, setAbierta] = useState<string | null>(null);
  const [tiendas, setTiendas] = useState<string[]>([]);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [dias, setDias] = useState<number[]>([]);
  const [guardando, setGuardando] = useState(false);

  // Alta de una dinámica nueva
  const [creando, setCreando] = useState(false);
  const [nName, setNName] = useState('');
  const [nKind, setNKind] = useState<'producto' | 'monto'>('producto');
  const [nMonto, setNMonto] = useState('');
  const [nNota, setNNota] = useState('');
  const [nSorteo, setNSorteo] = useState(true);
  const [nPideNombre, setNPideNombre] = useState(false);
  const [nPremios, setNPremios] = useState('');

  // Ajustes del indicador (U3). Antes se cambiaban por SQL, igual que las ventanas antes de 0029.
  const [ajustes, setAjustes] = useState<AjustesDinamica | null>(null);
  const [kpiLabel, setKpiLabel] = useState('');
  const [kpiMeta, setKpiMeta] = useState('');
  const [tope, setTope] = useState('1');
  const [marcas, setMarcas] = useState('');
  // Qué se pregunta de la boleta (0043). Son dos decisiones independientes: se puede pedir la
  // foto, el número, las dos o ninguna.
  const [pideFoto, setPideFoto] = useState(true);
  const [pideNumero, setPideNumero] = useState(false);
  const [guardandoAjustes, setGuardandoAjustes] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const ds = await listDinamicasDeCampania(campaignId);
      setDinamicas(ds);
      setVentanas(await listVentanas(ds.map((d) => d.id)));
      setPdvs(await listPdv(''));
      const a = await getAjustesDinamica(campaignId);
      setAjustes(a);
      setKpiLabel(a?.kpiLabel ?? '');
      setKpiMeta(a?.kpiMetaDia === null || a?.kpiMetaDia === undefined ? '' : String(a.kpiMetaDia));
      setTope(String(a?.maxPerClient ?? 1));
      setMarcas((a?.competitorBrands ?? []).join('\n'));
      setPideFoto(a?.pideFotoBoleta ?? true);
      setPideNumero(a?.pideNumeroBoleta ?? false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la configuración.');
    } finally {
      setCargando(false);
    }
  }, [campaignId]);

  useEffect(() => { void cargar(); }, [cargar]);

  function limpiar(): void {
    setAbierta(null); setTiendas([]); setDesde(''); setHasta(''); setDias([]);
  }

  async function agregar(dynamicId: string): Promise<void> {
    setError(null);
    setGuardando(true);
    try {
      // Sin tiendas elegidas se crea UNA ventana para todas: es un caso legítimo (una promoción
      // de campaña completa acotada solo por fechas) y no un descuido.
      const filas = tiendas.length > 0
        ? tiendas.map((pdvId) => ({
            dynamicId, pdvId, dateFrom: desde || null, dateTo: hasta || null, weekdays: dias,
          }))
        : [{ dynamicId, pdvId: null, dateFrom: desde || null, dateTo: hasta || null, weekdays: dias }];
      await crearVentanas(filas);
      setAviso(`Vigencia agregada en ${filas.length} ${filas.length === 1 ? 'caso' : 'tiendas'}.`);
      limpiar();
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar la vigencia.');
    } finally {
      setGuardando(false);
    }
  }

  async function guardarAjustes(): Promise<void> {
    setError(null);
    const meta = kpiMeta.trim() === '' ? null : Number(kpiMeta);
    if (meta !== null && (!Number.isInteger(meta) || meta < 1)) {
      setError('La meta diaria tiene que ser un número entero mayor que cero, o quedar vacía.');
      return;
    }
    const topeNum = Number(tope);
    if (!Number.isInteger(topeNum) || topeNum < 1) {
      setError('El tope por cliente tiene que ser un número entero mayor que cero.');
      return;
    }
    setGuardandoAjustes(true);
    try {
      await guardarAjustesDinamica(campaignId, {
        kpiLabel: kpiLabel.trim() === '' ? null : kpiLabel.trim(),
        kpiMetaDia: meta,
        maxPerClient: topeNum,
        competitorBrands: marcas.split('\n').map((m) => m.trim()).filter((m) => m !== ''),
        pideFotoBoleta: pideFoto,
        pideNumeroBoleta: pideNumero,
      });
      setAviso('Ajustes guardados. La columna del tablero de Hoy ya usa este nombre.');
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron guardar los ajustes.');
    } finally {
      setGuardandoAjustes(false);
    }
  }

  function limpiarAlta(): void {
    setCreando(false); setNName(''); setNKind('producto'); setNMonto('');
    setNNota(''); setNSorteo(true); setNPideNombre(false); setNPremios('');
  }

  /**
   * Da de alta la dinámica. Los premios se escriben uno por línea porque es como los dicta la
   * gente ("batea, paño, lavaseca") y porque una dinámica típica tiene tres, no treinta: un
   * editor de filas con botón de agregar sería más ceremonia que dato.
   */
  async function crear(): Promise<void> {
    setError(null);
    const premios = nPremios.split('\n').map((p) => p.trim()).filter((p) => p !== '');
    if (nName.trim() === '') { setError('Ponle un nombre a la dinámica.'); return; }
    if (nKind === 'monto' && nMonto.trim() === '') {
      setError('Una dinámica por monto necesita su monto mínimo.');
      return;
    }
    if (premios.length === 0) {
      // Se exige acá y no en el servidor: una dinámica sin premios se puede crear, pero el
      // mercadista no podría terminar ningún registro porque el premio es obligatorio en su
      // pantalla. Mejor pedirlo cuando todavía se puede escribir.
      setError('Agrega al menos un premio: sin premios nadie puede registrar un canje.');
      return;
    }
    setGuardando(true);
    try {
      await crearDinamica({
        campaignId,
        name: nName.trim(),
        triggerKind: nKind,
        minAmount: nKind === 'monto' ? Number(nMonto) : null,
        triggerNote: nNota.trim(),
        prizeSelection: nSorteo ? 'elegido' : 'esperado',
        requiresClientName: nPideNombre,
        premios,
      });
      setAviso(
        `Dinámica "${nName.trim()}" creada. ⚠️ Todavía NO aparece en ningún celular: `
        + 'agrégale una vigencia abajo para decir en qué tiendas y qué días corre.',
      );
      limpiarAlta();
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear la dinámica.');
    } finally {
      setGuardando(false);
    }
  }

  async function quitar(id: string): Promise<void> {
    setError(null);
    try {
      await borrarVentana(id);
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo quitar la vigencia.');
    }
  }

  if (cargando) return <p className="text-sm text-gray-500">Cargando dinámicas…</p>;

  return (
    <section className="rounded border p-4">
      <h2 className="mb-1 text-lg font-semibold">Dinámicas: dónde y cuándo</h2>
      <p className="mb-3 text-sm text-gray-600">
        La dinámica solo aparece en el celular de quien trabaja en las tiendas y los días
        marcados aquí.
      </p>

      {error && <p className="mb-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{error}</p>}
      {aviso && <p className="mb-2 rounded border border-blue-200 bg-blue-50 p-2 text-sm text-blue-800">{aviso}</p>}

      {/*
        Ajustes del indicador (U3). Hasta ahora se cambiaban por SQL, que es exactamente lo que
        pasaba con las ventanas antes de la 0029: decidir cómo se llama el indicador de una
        campaña dependía de quien tuviera acceso a la base.

        Va primero porque es lo que define cómo se LEE todo lo demás: la columna del tablero de
        Hoy se llama como diga acá.
      */}
      <details className="mb-4 rounded border bg-gray-50 p-3" open={ajustes === null}>
        <summary className="cursor-pointer text-sm font-medium">
          Indicador de la campaña
          {kpiLabel !== '' && <span className="ml-2 font-normal text-gray-600">· {kpiLabel}</span>}
        </summary>

        <label className="mt-3 block text-sm">
          Cómo se llama el indicador
          <input
            value={kpiLabel}
            onChange={(e) => setKpiLabel(e.target.value)}
            placeholder="Raspa y Gana"
            className="mt-1 w-full rounded border px-2 py-1"
          />
          <span className="text-xs text-gray-500">
            Es el título de la columna en el tablero de Hoy. Vacío = esta campaña no tiene dinámica
            y la columna no aplica (se ve un guion). Rotular «Canjes» lo que son raspaditas
            convierte el indicador en algo que nadie sabe leer.
          </span>
        </label>

        <div className="mt-2 flex flex-wrap gap-4">
          <label className="text-sm">
            Meta diaria por tienda
            <input
              type="number" min="1" step="1"
              value={kpiMeta}
              onChange={(e) => setKpiMeta(e.target.value)}
              className="mt-1 block w-28 rounded border px-2 py-1"
            />
            <span className="block text-xs text-gray-500">
              Vacío = sin meta. Con meta el tablero muestra <code>n/meta</code> y se pinta en ámbar
              cuando va por debajo.
            </span>
          </label>
          <label className="text-sm">
            Tope de canjes por cliente
            <input
              type="number" min="1" step="1"
              value={tope}
              onChange={(e) => setTope(e.target.value)}
              className="mt-1 block w-28 rounded border px-2 py-1"
            />
            <span className="block text-xs text-gray-500">
              En un raspa y gana es 1: con tope 1 y una sola dinámica, la pantalla del mercadista
              entra en modo simple.
            </span>
          </label>
        </div>

        <label className="mt-2 block text-sm">
          Marcas de la competencia, una por línea
          <textarea
            value={marcas}
            onChange={(e) => setMarcas(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded border px-2 py-1"
          />
          <span className="text-xs text-gray-500">
            Es lo que la impulsadora elige al registrar. Conviene incluir una opción del tipo
            «Ninguna: ya venía por la marca», que es lo que distingue una venta robada de una que
            ya estaba.
          </span>
        </label>

        <fieldset className="mt-3 rounded border p-3">
          <legend className="px-1 text-sm font-medium text-gray-800">
            Qué se pregunta de la boleta
          </legend>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={pideFoto}
              onChange={(e) => setPideFoto(e.target.checked)}
              className="mt-1"
            />
            <span>
              Pedir <strong>foto</strong> de la boleta
              <span className="block text-xs text-gray-500">
                Apagarla la oculta del formulario. Las fotos ya subidas se conservan. Cuando se
                muestra sigue siendo opcional.
              </span>
            </span>
          </label>
          <label className="mt-2 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={pideNumero}
              onChange={(e) => setPideNumero(e.target.checked)}
              className="mt-1"
            />
            <span>
              Pedir <strong>número</strong> de boleta
              <span className="block text-xs text-gray-500">
                Campo alfanumérico. Sirve donde no dejan usar el celular en sala: el número se
                apunta y se tipea después.
              </span>
            </span>
          </label>
          {!pideFoto && !pideNumero && (
            <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
              Con las dos apagadas, el canje se registra <strong>sin ninguna evidencia de la
              compra</strong>. Es válido —el premio entregado sigue siendo obligatorio— pero
              después no hay con qué contrastar el registro.
            </p>
          )}
        </fieldset>

        <button
          type="button"
          disabled={guardandoAjustes}
          onClick={() => void guardarAjustes()}
          className="mt-3 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {guardandoAjustes ? 'Guardando…' : 'Guardar ajustes'}
        </button>
      </details>

      {/* Alta de dinámica. Va ARRIBA de la lista y no al final: en una campaña sin dinámicas la
          lista está vacía, y un botón debajo de un "no hay nada" es lo primero que no se ve. */}
      <div className="mb-4">
        {!creando ? (
          <button
            type="button"
            onClick={() => setCreando(true)}
            className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white"
          >
            + Nueva dinámica
          </button>
        ) : (
          <div className="rounded border border-blue-200 bg-blue-50/40 p-3">
            <h3 className="mb-2 font-medium">Nueva dinámica</h3>

            <label className="mb-2 block text-sm">
              Nombre
              <input
                value={nName}
                onChange={(e) => setNName(e.target.value)}
                placeholder="Raspa y Gana con Downy Intense Fresh"
                className="mt-1 w-full rounded border px-2 py-1"
              />
              <span className="text-xs text-gray-500">
                Es lo que ve la impulsadora. El código interno se genera solo.
              </span>
            </label>

            <fieldset className="mb-2 text-sm">
              <legend className="mb-1">¿Qué la dispara?</legend>
              <label className="mr-4">
                <input
                  type="radio"
                  checked={nKind === 'producto'}
                  onChange={() => { setNKind('producto'); setNMonto(''); }}
                /> Llevar un producto
              </label>
              <label>
                <input
                  type="radio"
                  checked={nKind === 'monto'}
                  onChange={() => setNKind('monto')}
                /> Gastar un monto
              </label>
            </fieldset>

            {nKind === 'monto' && (
              <label className="mb-2 block text-sm">
                Monto mínimo (S/)
                <input
                  type="number" min="1" step="0.5"
                  value={nMonto}
                  onChange={(e) => setNMonto(e.target.value)}
                  className="mt-1 w-32 rounded border px-2 py-1"
                />
                <span className="ml-2 text-xs text-gray-500">
                  Con monto, la pantalla le pide capturar el importe de cada boleta.
                </span>
              </label>
            )}

            <label className="mb-2 block text-sm">
              Condición, tal como se lee en el celular
              <input
                value={nNota}
                onChange={(e) => setNNota(e.target.value)}
                placeholder="Por la compra de S/24 en Downy Intense Fresh. Solo la versión Fresh."
                className="mt-1 w-full rounded border px-2 py-1"
              />
            </label>

            <label className="mb-2 block text-sm">
              Premios, uno por línea
              <textarea
                value={nPremios}
                onChange={(e) => setNPremios(e.target.value)}
                rows={3}
                placeholder={'Batea\nPaño\nLavaseca'}
                className="mt-1 w-full rounded border px-2 py-1"
              />
              <span className="text-xs text-gray-500">
                Los que la campaña ya tenga no se duplican.
              </span>
            </label>

            <label className="mb-1 block text-sm">
              <input
                type="checkbox"
                checked={nSorteo}
                onChange={(e) => setNSorteo(e.target.checked)}
              />{' '}
              El premio sale al azar (raspa y gana, ruleta)
              <span className="block pl-5 text-xs text-gray-500">
                Si está marcado, la impulsadora elige qué salió. Si no, el premio ya está definido
                y el sistema avisa cuando se entrega otro por falta de stock.
              </span>
            </label>

            <label className="mb-3 block text-sm">
              <input
                type="checkbox"
                checked={nPideNombre}
                onChange={(e) => setNPideNombre(e.target.checked)}
              />{' '}
              Pedir el nombre del cliente
              <span className="block pl-5 text-xs text-gray-500">
                En una cola que avanza rápido conviene dejarlo apagado: un campo obligatorio que no
                interesa se llena con «cliente».
              </span>
            </label>

            <div className="flex gap-2">
              <button
                type="button"
                disabled={guardando}
                onClick={() => void crear()}
                className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {guardando ? 'Creando…' : 'Crear dinámica'}
              </button>
              <button
                type="button"
                onClick={limpiarAlta}
                className="rounded border px-3 py-2 text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {dinamicas.length === 0 && (
        <p className="text-sm text-gray-500">Esta campaña no tiene dinámicas configuradas.</p>
      )}

      {dinamicas.map((d) => {
        const suyas = ventanas.filter((v) => v.dynamic_id === d.id);
        return (
          <div key={d.id} className="mb-4 border-t pt-3 first:border-t-0 first:pt-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-medium">{d.name}</h3>
              <span className="text-xs text-gray-500">{d.code}{d.active ? '' : ' · inactiva'}</span>
            </div>

            {suyas.length === 0 ? (
              // Es la advertencia más importante de la pantalla: sin ventanas, la promoción se
              // ofrece en TODA la campaña. Ver §3 de la 0029.
              <p className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
                Sin vigencias: esta dinámica se ofrece en <strong>todas las tiendas</strong> de la
                campaña, <strong>todos los días</strong>. Agrega una vigencia para acotarla.
              </p>
            ) : (
              <ul className="mt-2 space-y-1">
                {suyas.map((v) => (
                  <li key={v.id} className="flex items-center justify-between gap-2 rounded bg-gray-50 px-2 py-1 text-sm">
                    <span>{describir(v)}</span>
                    <button
                      type="button"
                      onClick={() => void quitar(v.id)}
                      className="min-h-[36px] shrink-0 text-sm text-red-700 underline"
                    >
                      Quitar
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {abierta === d.id ? (
              <div className="mt-3 space-y-3 rounded border bg-white p-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Tiendas</p>
                  <p className="mb-1 text-xs text-gray-500">
                    Sin marcar ninguna, la vigencia vale para todas las tiendas.
                  </p>
                  <div className="max-h-48 overflow-y-auto rounded border p-2">
                    {pdvs.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 py-1 text-sm">
                        <input
                          type="checkbox"
                          checked={tiendas.includes(p.id)}
                          onChange={(e) => setTiendas((t) =>
                            e.target.checked ? [...t, p.id] : t.filter((x) => x !== p.id))}
                        />
                        {p.name} <span className="text-xs text-gray-400">{p.code}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <label className="text-sm">
                    <span className="block text-gray-700">Desde</span>
                    <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                      className="mt-1 rounded border px-2 py-1" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700">Hasta</span>
                    <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                      className="mt-1 rounded border px-2 py-1" />
                  </label>
                </div>

                <div>
                  <p className="text-sm text-gray-700">Días de la semana</p>
                  <p className="mb-1 text-xs text-gray-500">Sin marcar ninguno, vale todos los días.</p>
                  <div className="flex flex-wrap gap-2">
                    {DIAS.map((x) => (
                      <button
                        key={x.n}
                        type="button"
                        aria-pressed={dias.includes(x.n)}
                        onClick={() => setDias((ds) =>
                          ds.includes(x.n) ? ds.filter((y) => y !== x.n) : [...ds, x.n])}
                        className={`min-h-[40px] rounded border px-3 py-1 text-sm ${
                          dias.includes(x.n)
                            ? 'border-blue-600 bg-blue-600 text-white'
                            : 'border-gray-300 bg-white text-gray-700'
                        }`}
                      >
                        {x.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={guardando}
                    onClick={() => void agregar(d.id)}
                    className="min-h-[44px] rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
                  >
                    {guardando ? 'Guardando…' : 'Agregar vigencia'}
                  </button>
                  <button type="button" onClick={limpiar} className="min-h-[44px] rounded border px-4 py-2">
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { limpiar(); setAbierta(d.id); }}
                className="mt-2 min-h-[40px] text-sm text-blue-700 underline"
              >
                + Agregar vigencia
              </button>
            )}
          </div>
        );
      })}
    </section>
  );
}
